import { useState, useCallback, useEffect } from 'react';
import { authFetch } from '../services/auth';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export function useGuardCheckins() {
  const [guardSearchQuery, setGuardSearchQuery] = useState('');
  const [guardSearchResults, setGuardSearchResults] = useState([]);
  const [guardSearchLoading, setGuardSearchLoading] = useState(false);
  const [latestGuardCheckin, setLatestGuardCheckin] = useState(null);
  const [guardCheckins, setGuardCheckins] = useState([]);
  const [guardCheckinLoading, setGuardCheckinLoading] = useState(null);

  const loadGuardCheckins = useCallback(async () => {
    try {
      const [latestRes, historyRes] = await Promise.all([
        authFetch(`${API_URL}/guard-checkins/latest`),
        authFetch(`${API_URL}/guard-checkins?limit=20`)
      ]);
      if (latestRes.ok) setLatestGuardCheckin(await latestRes.json());
      if (historyRes.ok) setGuardCheckins(await historyRes.json());
    } catch (err) { console.error('Failed to load guard checkins:', err); }
  }, []);

  useEffect(() => { loadGuardCheckins(); }, [loadGuardCheckins]);

  const handleGuardSearch = async () => {
    if (!guardSearchQuery.trim()) return;
    setGuardSearchLoading(true);
    try {
      const res = await authFetch(`${API_URL}/guard-records/search?q=${encodeURIComponent(guardSearchQuery)}`);
      if (res.ok) setGuardSearchResults(await res.json());
    } catch (err) { console.error('Guard search failed:', err); }
    finally { setGuardSearchLoading(false); }
  };

  const handleGuardCheckin = async (guardRecordId) => {
    setGuardCheckinLoading(guardRecordId);
    try {
      const res = await authFetch(`${API_URL}/guard-checkins?guard_record_id=${guardRecordId}`, {
        method: 'POST'
      });
      if (res.ok) {
        const checkin = await res.json();
        toast.success(`${checkin.guard_name} checked in`);
        setLatestGuardCheckin(checkin);
        setGuardSearchResults([]);
        setGuardSearchQuery('');
        loadGuardCheckins();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Check-in failed');
      }
    } catch { toast.error('Check-in failed'); }
    finally { setGuardCheckinLoading(null); }
  };

  const handleGuardCheckout = async (checkinId) => {
    try {
      const res = await authFetch(`${API_URL}/guard-checkins/${checkinId}/checkout`, {
        method: 'POST'
      });
      if (res.ok) {
        toast.success('Guard checked out');
        loadGuardCheckins();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Check-out failed');
      }
    } catch { toast.error('Check-out failed'); }
  };

  return {
    guardSearchQuery, setGuardSearchQuery,
    guardSearchResults, guardSearchLoading,
    handleGuardSearch,
    handleGuardCheckin, guardCheckinLoading,
    latestGuardCheckin, guardCheckins,
    handleGuardCheckout,
  };
}
